import { useEffect, useState } from 'react';

//  Интерфейсы и типы данных

/**
 * Данные пользователя Telegram
 * @param id - Уникальный идентификатор пользователя
 * @param first_name - Имя (обязательно, ASCII)
 * @param last_name - Фамилия (обязательно, ASCII)
 * @param username - Юзернейм (опционально, ASCII)
 */
interface TelegramUser {
  id: number;
  first_name: string; // Теперь обязательно
  last_name: string; // Теперь обязательно
  username?: string;
}

/**
 * Инициализационные данные Telegram WebApp
 * @param user - Объект пользователя
 * @param query_id - Идентификатор запроса (опционально)
 * @param auth_date - Дата авторизации (опционально)
 * @param hash - Хеш для проверки данных (опционально)
 */
interface TelegramInitData {
  user?: TelegramUser;
  query_id?: string;
  auth_date?: number;
  hash?: string;
}

//  Вспомогательные функции

/**
 * Санитизация строки: преобразует не-ASCII символы в ASCII или удаляет их
 * @param str - Входная строка
 * @param defaultValue - Значение по умолчанию, если строка undefined
 * @returns Санитизированная строка
 */
const sanitizeString = (str?: string, defaultValue: string = ''): string => {
  if (!str) return defaultValue;
  // Удаляем или заменяем не-ASCII символы (коды > 127)
  return str
    .normalize('NFD') // Разбиваем символы с диакритикой (e.g., "é" → "e" + "´")
    .replace(/[\u0300-\u036f]/g, '') // Удаляем диакритические знаки
    .replace(/[^\x00-\x7F]/g, ''); // Удаляем все не-ASCII символы
};

// Основной хук

/**
 * Хук для работы с инициализационными данными Telegram WebApp
 * @returns {Object} initData - Парсированные данные
 * @returns {string} rawInitData - Сырые данные в строке
 * @returns {string} botToken - Токен бота из .env
 */
const useTelegramInitData = () => {
  // ============================================
  // Состояния хука
  // ============================================
  const [initData, setInitData] = useState<TelegramInitData | null>(null);
  const [rawInitData, setRawInitData] = useState<string | null>(null);

  // Эффект инициализации
  useEffect(() => {
    // Получаем глобальный объект Telegram WebApp
    const webApp = window.Telegram?.WebApp;

    if (webApp) {
      try {
        // Парсинг unsafe данных (без проверки подлинности)
        const unsafeData = webApp.initDataUnsafe;

        // Проверяем наличие user и id
        let user: TelegramUser | undefined;
        if (unsafeData?.user && typeof unsafeData.user.id === 'number') {
          user = {
            id: unsafeData.user.id,
            first_name: sanitizeString(unsafeData.user.first_name, ''),
            last_name: sanitizeString(unsafeData.user.last_name, ''),
            username: sanitizeString(unsafeData.user.username),
          };
        }

        // Устанавливаем структурированные данные
        setInitData({
          user,
          query_id: unsafeData?.query_id,
          auth_date: unsafeData?.auth_date,
          hash: unsafeData?.hash,
        });

        // Сохраняем сырые данные для возможных проверок
        setRawInitData(webApp.initData);
      } catch (error) {
        console.error('Error parsing Telegram data:', error);
      }
    }
  }, []); // Запускаем только при монтировании


  return {
    initData,     // Парсированные данные
    rawInitData,  // Сырая строка initData
    botToken: import.meta.env.VITE_TG_BOT_TOKEN, // Токен из окружения
  };
};

export default useTelegramInitData;